import i18n, { BackendModule, type InitOptions } from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';

export const FALLBACK_LANGUAGE = 'en';
export const DEFAULT_LANGUAGE = 'da';

interface SupportedLanguages {
  [key: string]: {}
}

interface IBootstrapTranslationsParams {
  languages: SupportedLanguages;
  /**
   * showTranslationKeys - shows translation keys on the UI, regardless of the selected language
   * */
  showTranslationKeys?: boolean;
  userLanguage?: string;
  /**
   * initOptions - i18n.init options
   * */
  initOptions?: InitOptions;
}

interface IBootstrapTranslationsV2Params {
  /**
   * showTranslationKeys - shows translation keys on the UI, regardless of the selected language
   * */
  showTranslationKeys?: boolean;
  language?: string;
  /**
   * initOptions - i18n.init options
   * */
  initOptions?: InitOptions;
  txBackend: BackendModule;
}

export const DEBUG_TRANSLATIONS_LOCAL_STORAGE_KEY = 'DEBUG_TRANSLATIONS';

export const bootstrapTranslations = (params: IBootstrapTranslationsParams) => {
  const {
    languages,
    showTranslationKeys = false,
    userLanguage = FALLBACK_LANGUAGE,
    initOptions = {},
  } = params;
  const resources = Object.keys(languages).reduce((acc: { [key: string]: {} }, curr) => {
    acc[curr] = { translation: languages[curr] };
    return acc;
  }, {});

  // @ts-ignore
  i18n.SHOW_TRANSLATION_KEYS = showTranslationKeys;
  i18n.use(initReactI18next).init({
    resources,
    lng: showTranslationKeys ? '' : userLanguage,
    fallbackLng: showTranslationKeys ? false : FALLBACK_LANGUAGE,
    interpolation: { escapeValue: false },
    ...initOptions,
  });
};

export const bootstrapTranslationsV2 = (params: IBootstrapTranslationsV2Params) => {
  const {
    showTranslationKeys = false,
    language = FALLBACK_LANGUAGE,
    txBackend,
    initOptions = {},
  } = params;

  // @ts-ignore
  i18n.SHOW_TRANSLATION_KEYS = showTranslationKeys;

  return i18n
    .use(txBackend)
    .use(initReactI18next)
    .init({
      lng: showTranslationKeys ? '' : language,
      fallbackLng: showTranslationKeys ? false : FALLBACK_LANGUAGE,
      interpolation: { escapeValue: false },
      ...initOptions,
    });
};

export const getBrowserLanguage = () => navigator.language.split('-')[0];

const setLanguage = (lang: string) => {
  // @ts-ignore
  if (i18n.SHOW_TRANSLATION_KEYS) {
    return;
  }
  i18n.changeLanguage(lang);
};

export const useTranslations = () => {
  const currentLanguage = i18n.language;
  const { t } = useTranslation();
  return { setLanguage, currentLanguage, t };
};

export { type TFunction } from 'i18next';
